import clsx from 'clsx'
import {Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState} from 'react'
import {Button} from 'react-bootstrap-v5'
import Modal from 'react-bootstrap/Modal'
import {useIntl} from 'react-intl'
import Tooltip from 'react-tooltip-lite'
import {useDispatch} from 'react-redux'
import {
  buySubscription,
  cancelAddonSubscription,
  customPlanRequest,
  getAllSubscription,
  selectPricingLoading,
  selectSelecteesSubscription,
  selectSubscriptionPlan,
  setSelectedSubscription,
} from './redux/pricingRedux'
import {useAppSelector} from '../../../setup/redux/hooks'
import {SubscriptionPlanModel} from './models/subscriptionPlanModel'

import {
  CurrencyCode,
  CUSTOM_PLAN,
  PAY_AS_U_GO_PLAN,
  PlanType,
  PriceType,
  STORAGE_ADDONS,
} from './pricing.constant'
import {useHistory} from 'react-router-dom'
import {selectUser} from '../../modules/auth'
import {COUNTRY_CODES} from '../../modules/profile/constants'
import {PAYMENT_METHOD} from '../../shared/constants'
import PaymentModal from '../../components/paymentModal'
import {displayAmountBaseOnCurrency} from './utils'
import {formatNumber, formatNumberWithCommas} from '../../shared/utils'
import {getAll, selectAllService} from '../../modules/services/redux/serviceRedux'

const PricingPage: FC = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const history = useHistory()
  const didRequest = useRef(true)
  const user = useAppSelector(selectUser)
  const [planType, setPlanType] = useState<PlanType | string>(PlanType.PAY_AS_U_GO)
  const [isShowPaymentDetail, setIsShowPaymentDetail] = useState(false)
  const [isShowCancelConfirm, setIsShowCancelConfirm] = useState(false)
  const [cancelTargetPlan, setCancelTargetPlan] = useState<SubscriptionPlanModel | undefined>(
    undefined
  )
  const [cancelReason, setCancelReason] = useState('')
  const [cancelReasonTouched, setCancelReasonTouched] = useState(false)
  const [isShowCancelAddonConfirm, setIsShowCancelAddonConfirm] = useState(false)
  const [cancelAddonTarget, setCancelAddonTarget] = useState<{
    plan: SubscriptionPlanModel
    subscriptionId: string
  } | null>(null)
  const [isShowContactModal, setIsShowContactModal] = useState(false)
  const [currency, setCurrency] = useState(
    user?.countryCode === COUNTRY_CODES.JP ? CurrencyCode.JPY : CurrencyCode.USD
  )
  const plans: SubscriptionPlanModel[] = useAppSelector(selectSubscriptionPlan)

  const selectedPlan: SubscriptionPlanModel | undefined = useAppSelector(
    selectSelecteesSubscription
  )
  const loading: boolean = useAppSelector(selectPricingLoading)
  const services = useAppSelector(selectAllService)
  const [selectedServiceId, setSelectedServiceId] = useState<number | undefined>(undefined)
  const [customActionDisabled, setCustomActionDisabled] = useState(false)
  const subscriptionGroups = useMemo(
    () => [
      {type: PlanType.SUBSCRIPTION_BASIC, title: 'Basic'},
      {type: PlanType.SUBSCRIPTION_STANDARD, title: 'Standard'},
      {type: PlanType.SUBSCRIPTION_PREMIUM, title: 'Premium'},
    ],
    []
  )
  const payAsGoGroups = useMemo(
    () => [
      {
        key: 'PAY',
        title: 'Pay_as_u_go',
        matcher: (plan: SubscriptionPlanModel) => plan.id === PAY_AS_U_GO_PLAN,
      },
      {
        key: 'BUNDLE',
        title: 'Bundles',
        matcher: (plan: SubscriptionPlanModel) =>
          plan.type === PlanType.PAY_AS_U_GO && plan.id !== PAY_AS_U_GO_PLAN,
      },
    ],
    []
  )
  const [groupSelections, setGroupSelections] = useState<Record<string, number | undefined>>({})
  const [payGoSelections, setPayGoSelections] = useState<Record<string, number | undefined>>({})
  const [hoveredPlanToggle, setHoveredPlanToggle] = useState<PlanType | string | undefined>(
    undefined
  )
  const [hoveredPlanId, setHoveredPlanId] = useState<number | null>(null)
  const [contactSubmitAttempted, setContactSubmitAttempted] = useState(false)
  const [contactForm, setContactForm] = useState({
    companyName: '',
    companyAddress: '',
    businessField: '',
    companySize: '',
    processingVolume: '',
    message: '',
  })
  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId),
    [services, selectedServiceId]
  )
  const isVirtualStaging = useMemo(
    () => selectedService?.name?.toLowerCase() === 'virtual_staging',
    [selectedService?.name]
  )
  const planToggleOptions = useMemo(
    () =>
      isVirtualStaging
        ? [PlanType.PAY_AS_U_GO, PlanType.SUBSCRIPTION]
        : [PlanType.PAY_AS_U_GO, PlanType.SUBSCRIPTION, PlanType.ADD_ON],
    [isVirtualStaging]
  )
  const businessFieldOptions = useMemo(
    () => [
      'Real Estate Photography',
      'Property Management',
      'Real Estate Brokerage',
      'Short-term Rentals / Hospitality',
      'Architecture & Design',
      'Marketing Agency',
      '3D Visualization',
      'Other',
    ],
    []
  )
  const companySizeOptions = useMemo(
    () => ['1-10', '11-50', '51-200', '201-500', '501-1,000', '1,000+'],
    []
  )
  const processingVolumeOptions = useMemo(
    () => ['0-100', '101-500', '501-2,000', '2,001-10,000', '10,001+'],
    []
  )
  const cancelReasonOptions = useMemo(
    () => [
      'MODAL.PLAN.cancel_reason.option.too_expensive',
      'MODAL.PLAN.cancel_reason.option.not_using_enough',
      'MODAL.PLAN.cancel_reason.option.missing_features',
      'MODAL.PLAN.cancel_reason.option.switching_service',
      'MODAL.PLAN.cancel_reason.option.other',
    ],
    []
  )
  const currentPlan = useMemo(
    () => plans.find((plan) => plan.id === user?.planId),
    [plans, user?.planId]
  )
  const isContactFormValid = useMemo(() => {
    const requiredFields = [
      contactForm.companyName,
      contactForm.companyAddress,
      contactForm.businessField,
      contactForm.companySize,
      contactForm.processingVolume,
    ]
    return requiredFields.every((value) => value.trim().length > 0)
  }, [contactForm])
  const isContactFieldInvalid = (value: string) => contactSubmitAttempted && !value.trim()
  const handleClose = () => {
    history.push('/')
  }

  const handleUpgradePlanClick = () => {
    if (!selectedPlan) {
      return
    }
    handlePlanAction(selectedPlan)
  }

  const handlePlanAction = (plan: SubscriptionPlanModel) => {
    dispatch(setSelectedSubscription(plan))
    if (plan.id === PAY_AS_U_GO_PLAN) {
      dispatch(buySubscription({currency, paymentMethod: PAYMENT_METHOD.VISA}))
    } else if (plan.id === CUSTOM_PLAN) {
      if (customActionDisabled) return
      setContactSubmitAttempted(false)
      setContactForm({
        companyName: user?.companyName ?? '',
        companyAddress: user?.companyAddress ?? '',
        businessField: '',
        companySize: '',
        processingVolume: '',
        message: '',
      })
      setIsShowContactModal(true)
    } else {
      setIsShowPaymentDetail(true)
    }
  }

  const buildCustomPlanMessage = () => {
    const formatLine = (label: string, value: string) =>
      `${label}: ${value?.trim() ? value.trim() : '-'}`
    const baseMessage = intl.formatMessage({id: 'MODAL.PLAN.request_message'})
    const details = [
      formatLine(intl.formatMessage({id: 'MODAL.PLAN.company_name'}), contactForm.companyName),
      formatLine(
        intl.formatMessage({id: 'MODAL.PLAN.company_address'}),
        contactForm.companyAddress
      ),
      formatLine(intl.formatMessage({id: 'MODAL.PLAN.business_field'}), contactForm.businessField),
      formatLine(intl.formatMessage({id: 'MODAL.PLAN.company_size'}), contactForm.companySize),
      formatLine(
        intl.formatMessage({id: 'MODAL.PLAN.processing_volume'}),
        contactForm.processingVolume
      ),
      formatLine(intl.formatMessage({id: 'MODAL.PLAN.message'}), contactForm.message),
    ].join('\n')
    return `${baseMessage}\n\n${details}`
  }

  const handleSubmitContactRequest = () => {
    if (!isContactFormValid) {
      setContactSubmitAttempted(true)
      return
    }
    if (customActionDisabled) return
    setCustomActionDisabled(true)
    setTimeout(() => setCustomActionDisabled(false), 30000)
    dispatch(customPlanRequest({message: buildCustomPlanMessage()}))
    setIsShowContactModal(false)
  }

  const openCancelConfirm = (targetPlan?: SubscriptionPlanModel) => {
    const payAsYouGoPlan = targetPlan ?? plans.find((plan) => plan.id === PAY_AS_U_GO_PLAN)
    if (!payAsYouGoPlan) return
    setCancelTargetPlan(payAsYouGoPlan)
    setCancelReason('')
    setCancelReasonTouched(false)
    setIsShowCancelConfirm(true)
  }

  const handleCloseCancelConfirm = () => {
    setIsShowCancelConfirm(false)
    setCancelTargetPlan(undefined)
    setCancelReason('')
    setCancelReasonTouched(false)
  }

  const handleConfirmCancelSubscription = () => {
    if (!cancelReason) {
      setCancelReasonTouched(true)
      return
    }
    if (!cancelTargetPlan) {
      handleCloseCancelConfirm()
      return
    }
    const targetPlan = cancelTargetPlan
    handleCloseCancelConfirm()
    handlePlanAction(targetPlan)
  }

  const handleContactFieldChange = (field: keyof typeof contactForm, value: string) => {
    setContactForm((prev) => ({...prev, [field]: value}))
  }

  const handlePlanTypeChange = (newPlanType: PlanType | string) => {
    if (newPlanType === 'SUBSCRIPTION') {
      setPlanType('SUBSCRIPTION')
    } else {
      setPlanType(newPlanType as PlanType)
    }
  }

  const getFilteredPlans = () => {
    if (planType === 'SUBSCRIPTION') {
      return plans.filter((item) => item.type.startsWith('SUBSCRIPTION'))
    }
    return plans.filter((item) => item.type === planType)
  }

  // Check if an addon plan is currently active for the user
  const isAddonActive = (plan: SubscriptionPlanModel) => {
    if (plan.type !== PlanType.ADD_ON) return false
    return (
      user?.activeAddOns?.some((addon) => addon.planId === plan.id || addon.name === plan.name) ??
      false
    )
  }

  // Check if a plan is a storage addon (by name pattern since we don't have slug)
  const isStorageAddonPlan = (plan: SubscriptionPlanModel): boolean => {
    return plan.type === PlanType.ADD_ON && plan.name.toLowerCase().includes('storage')
  }

  // Check if another storage addon is already active (not the current one being viewed)
  const hasOtherStorageAddonActive = (plan: SubscriptionPlanModel): boolean => {
    // Only applies to storage addon plans
    if (!isStorageAddonPlan(plan)) return false

    // Check if user has any storage addon active
    const activeStorageAddon = user?.activeAddOns?.find((addon) =>
      STORAGE_ADDONS.includes(addon.name as any)
    )

    if (!activeStorageAddon) return false

    // If this plan is the one that's already active, return false
    if (activeStorageAddon.planId === plan.id) return false

    // User has a different storage addon active
    return true
  }

  // Get the subscriptionId for an active addon plan
  const getAddonSubscriptionId = (plan: SubscriptionPlanModel): string | undefined => {
    if (plan.type !== PlanType.ADD_ON) return undefined
    const activeAddon = user?.activeAddOns?.find(
      (addon) => addon.planId === plan.id || addon.name === plan.name
    )
    return activeAddon?.subscriptionId
  }

  // Open cancel addon confirmation modal
  const openCancelAddonConfirm = (plan: SubscriptionPlanModel) => {
    const subscriptionId = getAddonSubscriptionId(plan)
    if (!subscriptionId) return
    setCancelAddonTarget({plan, subscriptionId})
    setIsShowCancelAddonConfirm(true)
  }

  // Handle cancel addon confirmation
  const handleConfirmCancelAddon = () => {
    if (!cancelAddonTarget) return
    dispatch(
      cancelAddonSubscription({
        subscriptionId: cancelAddonTarget.subscriptionId,
        planId: cancelAddonTarget.plan.id,
      })
    )
    setIsShowCancelAddonConfirm(false)
    setCancelAddonTarget(null)
  }

  // Close cancel addon modal
  const handleCloseCancelAddonConfirm = () => {
    setIsShowCancelAddonConfirm(false)
    setCancelAddonTarget(null)
  }

  const renderPlanBag = (plan: SubscriptionPlanModel, price: any | undefined) => {
    if (!price || [CUSTOM_PLAN, PAY_AS_U_GO_PLAN].includes(plan.id))
      return intl.formatMessage({id: 'MODAL.PLAN.CURRENCY.' + plan.id})
    if (PlanType.ADD_ON === plan.type) {
      return plan.desc
    }
    return (
      '+' +
      new Intl.NumberFormat(intl.locale, {style: 'decimal'}).format(plan.point) +
      intl.formatMessage({id: 'SYSTEM.CURRENCY'})
    )
  }
  const renderPrice = (plan: SubscriptionPlanModel, price: any | undefined) => {
    if (!price) {
      return plan.desc || plan.shortDesc || ''
    }
    const serviceRate = selectedService?.price || 1.2 * (currency === CurrencyCode.JPY ? 125 : 1)
    let unitCost =
      currency === CurrencyCode.JPY
        ? Math.round((price.price / (plan.point / serviceRate)) * 100) / 100
        : formatNumber(price.price / (plan.point / serviceRate) / 100)

    if (plan.id === PAY_AS_U_GO_PLAN) {
      unitCost = (selectedService?.price || 1.2) * (currency === CurrencyCode.JPY ? 125 : 1)
    }
    return intl.formatMessage(
      {id: 'MODAL.PLAN.rate'},
      {
        currency: intl.formatMessage({id: currency, defaultMessage: currency}),
        rate: unitCost,
      }
    )
  }
  const subscriptionGroupDefs = useMemo(
    () =>
      subscriptionGroups.map((group) => {
        if (!isVirtualStaging) {
          return {
            key: group.type,
            title: group.title,
            plans: plans.filter((plan) => plan.type === group.type),
          }
        }
        let filteredPlans = plans.filter((plan) => plan.type === group.type)
        if (group.type === PlanType.SUBSCRIPTION_BASIC) {
          filteredPlans = filteredPlans.filter((plan) => plan.point === 60)
        }
        if (group.type === PlanType.SUBSCRIPTION_STANDARD) {
          filteredPlans = filteredPlans.filter((plan) => plan.point === 240)
        }
        if (group.type === PlanType.SUBSCRIPTION_PREMIUM) {
          filteredPlans = plans.filter(
            (plan) => plan.type === PlanType.SUBSCRIPTION_STANDARD && plan.point === 480
          )
        }
        return {
          key: group.type,
          title: group.title,
          plans: filteredPlans,
        }
      }),
    [isVirtualStaging, plans, subscriptionGroups]
  )
  console.log('subscriptionGroupDefs', subscriptionGroupDefs)
  const payAsYouGoGroupDefs = useMemo(
    () =>
      payAsGoGroups.map((group) => ({
        key: group.key,
        title: group.title,
        plans: plans.filter((plan) => group.matcher(plan)),
      })),
    [plans, payAsGoGroups]
  )

  useEffect(() => {
    if (didRequest.current) {
      dispatch(getAllSubscription())
      didRequest.current = true
    }
    dispatch(getAll())
    const userPlanType = user?.planType as PlanType | undefined
    if (userPlanType && userPlanType.startsWith('SUBSCRIPTION')) {
      setPlanType('SUBSCRIPTION')
    } else {
      setPlanType(userPlanType ?? PlanType.PAY_AS_U_GO)
    }

    if (user && user.countryCode === COUNTRY_CODES.JP) {
      setCurrency(CurrencyCode.JPY)
    }
  }, [user, dispatch])

  useEffect(() => {
    if (!selectedServiceId && services.length > 0) {
      setSelectedServiceId(services[0].id)
    }
  }, [services, selectedServiceId])

  useEffect(() => {
    if (isVirtualStaging && planType === PlanType.ADD_ON) {
      setPlanType(PlanType.PAY_AS_U_GO)
    }
  }, [isVirtualStaging, planType])

  useEffect(() => {
    if (!plans || plans.length === 0) return
    setGroupSelections((prev) => {
      const next = {...prev}
      let changed = false
      subscriptionGroups.forEach((group) => {
        if (!next[group.type]) {
          const firstPlan = plans.find((p) => p.type === group.type)
          if (firstPlan) {
            next[group.type] = firstPlan.id
            changed = true
          }
        }
      })
      return changed ? next : prev
    })
    setPayGoSelections((prev) => {
      const next = {...prev}
      let changed = false
      payAsGoGroups.forEach((group) => {
        if (!next[group.key]) {
          const firstPlan = plans.find((p) => group.matcher(p))
          if (firstPlan) {
            next[group.key] = firstPlan.id
            changed = true
          }
        }
      })
      return changed ? next : prev
    })
  }, [plans, subscriptionGroups, payAsGoGroups])

  useEffect(() => {
    if (!selectedPlan) return
    const subscriptionGroup = subscriptionGroups.find((group) => group.type === selectedPlan.type)
    if (subscriptionGroup) {
      setGroupSelections((prev) =>
        prev[subscriptionGroup.type] === selectedPlan.id
          ? prev
          : {...prev, [subscriptionGroup.type]: selectedPlan.id}
      )
      return
    }
    if (selectedPlan.type === PlanType.PAY_AS_U_GO) {
      const matchedGroup = payAsGoGroups.find((group) => group.matcher(selectedPlan))
      if (!matchedGroup) return
      setPayGoSelections((prev) =>
        prev[matchedGroup.key] === selectedPlan.id
          ? prev
          : {...prev, [matchedGroup.key]: selectedPlan.id}
      )
    }
  }, [payAsGoGroups, selectedPlan, subscriptionGroups])

  useEffect(() => {
    if (selectedPlan || planType !== 'SUBSCRIPTION') return
    const firstGroupId = subscriptionGroups[0]?.type
    const firstPlan =
      plans.find((p) => p.id === groupSelections[firstGroupId]) ||
      plans.find((p) => p.type === firstGroupId)
    if (firstPlan) {
      dispatch(setSelectedSubscription(firstPlan))
    }
  }, [dispatch, groupSelections, planType, plans, selectedPlan, subscriptionGroups])

  useEffect(() => {
    if (planType !== PlanType.PAY_AS_U_GO) return
    const payPlans = plans.filter((p) => p.type === PlanType.PAY_AS_U_GO)
    const selectedPayPlan = payPlans.find((p) => p.id === selectedPlan?.id)
    if (selectedPayPlan) return
    const chosenPlan = payPlans.find((p) => Object.values(payGoSelections).includes(p.id))
    const fallbackPlan = chosenPlan || payPlans[0]
    if (fallbackPlan && fallbackPlan.id !== selectedPlan?.id) {
      dispatch(setSelectedSubscription(fallbackPlan))
    }
  }, [dispatch, payGoSelections, planType, plans, selectedPlan])

  const renderFeatures = (plan: SubscriptionPlanModel) => {
    if (!plan.features || plan.features.length === 0) return null
    return (
      <ul className='list-unstyled mb-4'>
        {plan.features.slice(0, 3).map((feature, idx) => (
          <li key={idx} className='d-flex align-items-center mb-2 text-gray-700'>
            <span className='bullet bullet-dot bg-primary me-3'></span>
            <span className='fw-semibold'>{feature.desc}</span>
          </li>
        ))}
      </ul>
    )
  }

  const renderActionButton = (plan: SubscriptionPlanModel) => {
    const isCurrentPlan = plan.id === user?.planId && plan.id !== PAY_AS_U_GO_PLAN
    const isPayAsYouGoCurrent = plan.id === PAY_AS_U_GO_PLAN && user?.planId === PAY_AS_U_GO_PLAN
    const isSwitchingSubscriptionToPayAsYouGo =
      plan.id === PAY_AS_U_GO_PLAN &&
      Boolean(currentPlan?.type?.startsWith('SUBSCRIPTION')) &&
      user?.planId !== PAY_AS_U_GO_PLAN
    const isComparableSubscription =
      currentPlan &&
      currentPlan.type.startsWith('SUBSCRIPTION') &&
      plan.type.startsWith('SUBSCRIPTION')
    const isDowngrade = Boolean(
      isComparableSubscription && currentPlan && plan.point < currentPlan.point
    )
    const isPayAsYouGoPlan = plan.type === PlanType.PAY_AS_U_GO
    const isActiveAddon = isAddonActive(plan)
    const isOtherStorageAddonActive = hasOtherStorageAddonActive(plan)
    const label = isCurrentPlan
      ? intl.formatMessage({id: 'MODAL.PLAN.cancel_subscription'})
      : plan.id === CUSTOM_PLAN
      ? intl.formatMessage({id: 'MODAL.PLAN.call_btn'})
      : plan.type === PlanType.ADD_ON
      ? isActiveAddon
        ? intl.formatMessage({id: 'MODAL.PLAN.cancel_addon'})
        : intl.formatMessage({id: 'MODAL.PLAN.subscribe_btn'})
      : isPayAsYouGoPlan && plan.id === PAY_AS_U_GO_PLAN
      ? intl.formatMessage({id: 'MODAL.PLAN.select_btn'})
      : isPayAsYouGoPlan
      ? intl.formatMessage({id: 'MODAL.PLAN.buy_packages'})
      : isDowngrade
      ? intl.formatMessage({id: 'MODAL.PLAN.downgrade_btn'})
      : intl.formatMessage({id: 'MODAL.PLAN.upgrade_btn'})

    // If another storage addon is active, show disabled button with tooltip
    if (isOtherStorageAddonActive && !isActiveAddon) {
      return (
        <Tooltip
          content={
            <div>{intl.formatMessage({id: 'MODAL.PLAN.storage_addon_limit_tooltip'})}</div>
          }
          className='tooltips'
        >
          <div className='mt-auto w-100'>
            <Button
              variant='secondary'
              className='fw-bold w-100 py-3'
              disabled
            >
              {label}
            </Button>
          </div>
        </Tooltip>
      )
    }

    return (
      <Button
        variant={isActiveAddon ? 'light-danger' : 'primary'}
        className={`mt-auto fw-bold w-100 py-3 ${isActiveAddon ? 'text-danger border-danger' : ''}`}
        disabled={isPayAsYouGoCurrent}
        onClick={() => {
          if (isActiveAddon) {
            openCancelAddonConfirm(plan)
            return
          }
          if (isCurrentPlan) {
            openCancelConfirm()
            return
          }
          if (isSwitchingSubscriptionToPayAsYouGo) {
            openCancelConfirm(plan)
            return
          }
          handlePlanAction(plan)
        }}
      >
        {isActiveAddon && <i className='bi bi-x-circle me-2'></i>}
        {label}
      </Button>
    )
  }

  const getPlanCardStyles = (isSelected: boolean, isHovered: boolean) => {
    const isHoverStyle = isHovered
    return {
      className: clsx(
        'p-6 rounded-4 shadow-sm border h-100 d-flex flex-column ribbon',
        isHoverStyle
          ? 'border-primary border-2 bg-light-primary shadow-lg'
          : isSelected
          ? 'border-primary border-2 border-dashed bg-white shadow-lg'
          : 'bg-white'
      ),
      style: {
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        transform: isHoverStyle
          ? 'translateY(-4px)'
          : isSelected
          ? 'translateY(-2px)'
          : 'translateY(0)',
        boxShadow: isHoverStyle
          ? '0 0.75rem 1.5rem rgba(0, 0, 0, 0.12)'
          : isSelected
          ? '0 0 0 0.2rem rgba(25, 135, 84, 0.12), 0 0.75rem 1.5rem rgba(0, 0, 0, 0.08)'
          : undefined,
        backgroundImage: undefined,
      },
    }
  }

  const renderGroupedCards = (
    groups: Array<{key: string; title: string; plans: SubscriptionPlanModel[]}>,
    selections: Record<string, number | undefined>,
    setSelections: Dispatch<SetStateAction<Record<string, number | undefined>>>
  ) => (
    <div className='row mt-5 g-5 justify-content-center'>
      {groups.map((group, index) => {
        const filteredPlans = group.plans
          .filter((p) => p.id !== CUSTOM_PLAN)
          .slice()
          .sort((a, b) => (a.point ?? 0) - (b.point ?? 0))

        if (filteredPlans.length === 0) {
          return null
        }
        const selectedId = selections[group.key] ?? filteredPlans[0].id
        const plan = filteredPlans.find((p) => p.id === selectedId) || filteredPlans[0]
        const priceData = plan.priceData?.find((price) => price.currency === currency)
        const price = plan.id === CUSTOM_PLAN ? undefined : priceData
        const isSelected = plan.id === user?.planId
        const isHovered = hoveredPlanId === plan.id
        const cardStyles = getPlanCardStyles(isSelected, isHovered)
        const isPayAsYouGo = plan.id === PAY_AS_U_GO_PLAN

        const rates: string = renderPrice(plan, price)
        const totalPrice =
          ![CUSTOM_PLAN, PAY_AS_U_GO_PLAN].includes(plan.id) && price
            ? displayAmountBaseOnCurrency(price.price, currency)
            : rates
        return (
          <div className='col-12 col-xl-4' key={`${group.key}-${index}`}>
            <div
              className={cardStyles.className}
              style={cardStyles.style}
              role='button'
              onClick={() => dispatch(setSelectedSubscription(plan))}
              onMouseEnter={() => setHoveredPlanId(plan.id)}
              onMouseLeave={() => setHoveredPlanId(null)}
            >
              {isSelected && (
                <div
                  className='ribbon-label bg-primary text-white fw-bold text-uppercase fs-8'
                  style={{
                    width: '140px',
                    top: '-5px',
                    left: '30%',
                    borderRadius: '5px',
                  }}
                >
                  {intl.formatMessage({id: 'MODAL.PLAN.current_plan'})}
                </div>
              )}
              <div className='text-primary text-uppercase fw-bold fs-8 letter-spacing-1 mb-2'>
                {intl.formatMessage({id: 'HEADER.Plan.' + group.title})}
              </div>
              {filteredPlans.length > 1 && (
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <h3 className='fw-bolder fs-2 mb-0'>{plan.name}</h3>
                </div>
              )}
              <div className='d-flex align-items-center justify-content-between gap-2 mb-3'>
                <div className=' fw-bolder text-dark' style={{fontSize: '1.6rem'}}>
                  {rates}
                </div>
                {!isPayAsYouGo && (
                  <span className='badge badge-light-primary text-uppercase'>
                    {formatNumberWithCommas(plan.point / (selectedService?.price || 1.2))}{' '}
                    {intl.formatMessage({id: 'MODAL.PLAN.PHOTOS'})}
                  </span>
                )}
              </div>
              {/* <div className='mb-3 text-muted fw-semibold fs-7'>{plan.desc || plan.shortDesc}</div> */}
              {!isPayAsYouGo && filteredPlans.length > 1 && (
                <div className='mb-4'>
                  <label className='form-label fw-semibold text-uppercase fs-8 text-muted mb-1 '>
                    {intl.formatMessage({id: 'MODAL.PLAN.BUNDLE_LABEL'})}
                  </label>
                  <select
                    className='form-select form-select-solid'
                    value={selectedId}
                    onChange={(e) => {
                      const nextPlan = group.plans.find((p) => p.id === Number(e.target.value))
                      setSelections((prev) => ({...prev, [group.key]: Number(e.target.value)}))
                      if (nextPlan) {
                        dispatch(setSelectedSubscription(nextPlan))
                      }
                    }}
                  >
                    {filteredPlans.map((option) => (
                      <option value={option.id} key={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div
                className='bg-light p-4 rounded-3 mb-3'
                style={{
                  marginTop: isPayAsYouGo ? '6rem' : 'auto',
                }}
              >
                <div className='d-flex justify-content-between text-uppercase fw-bold fs-8 text-primary mb-2'>
                  <span>
                    {intl.formatMessage({id: 'MODAL.PLAN.credits', defaultMessage: 'Credits'})}
                  </span>
                  <span>
                    {intl.formatMessage({id: 'MODAL.PLAN.price_label', defaultMessage: 'Price'})}
                  </span>
                </div>

                <div className='d-flex justify-content-between align-items-center fw-bolder fs-4 text-dark'>
                  <span>
                    +{!isPayAsYouGo ? Number(plan.point) : Number(selectedService?.price ?? 1.2)}
                  </span>
                  <span>
                    {totalPrice}{' '}
                    <span className='text-muted fw-normal fs-7'>
                      {plan.priceType === PriceType.SUBSCRIPTION
                        ? intl.formatMessage({id: 'MODAL.PLAN.monthly'})
                        : ''}
                    </span>
                  </span>
                </div>
              </div>
              {/* {renderFeatures(plan)} */}
              {renderActionButton(plan)}
            </div>
          </div>
        )
      })}
    </div>
  )

  const renderAddonCards = () => (
    <div className='row mt-5 g-5'>
      {getFilteredPlans().map((plan, index) => {
        const priceData = plan.priceData?.find((price) => price.currency === currency)
        const price = plan.id === CUSTOM_PLAN ? undefined : priceData
        const isSelected = plan.id === user?.planId
        const isActiveAddon = isAddonActive(plan)
        const isHovered = hoveredPlanId === plan.id
        const cardStyles = getPlanCardStyles(isSelected || isActiveAddon, isHovered)
        const totalPrice =
          ![CUSTOM_PLAN, PAY_AS_U_GO_PLAN].includes(plan.id) && price
            ? displayAmountBaseOnCurrency(price.price, currency)
            : intl.formatMessage({id: 'MODAL.PLAN.' + plan.id})
        const rate = renderPrice(plan, price)
        return (
          <div className='col-12 col-xl-6' key={index}>
            <div
              className={cardStyles.className}
              style={cardStyles.style}
              onClick={() => dispatch(setSelectedSubscription(plan))}
              role='button'
              onMouseEnter={() => setHoveredPlanId(plan.id)}
              onMouseLeave={() => setHoveredPlanId(null)}
            >
              {(isSelected || isActiveAddon) && (
                <div
                  className={`ribbon-label ${
                    isActiveAddon ? 'bg-success' : 'bg-primary'
                  } text-white fw-bold rounded text-uppercase fs-8`}
                  style={{
                    top: '-10px',
                  }}
                >
                  {isActiveAddon
                    ? intl.formatMessage({id: 'MODAL.PLAN.subscribed'})
                    : intl.formatMessage({id: 'MODAL.PLAN.current_plan'})}
                </div>
              )}
              <div className='text-primary text-uppercase fw-bold fs-8 letter-spacing-1 mb-2'>
                {renderPlanBag(plan, price)}
              </div>
              <h3 className='fw-bolder fs-2 mb-3'>
                {plan.id === PAY_AS_U_GO_PLAN
                  ? intl.formatMessage({id: 'MODAL.PLAN.PAY_AS_U_GO_PLAN'})
                  : plan.name}
              </h3>
              <div className='d-flex align-items-baseline gap-2 mb-3'>
                <div className='fs-1 fw-bolder text-dark'>{totalPrice}</div>
                <span className='text-muted'>
                  {plan.priceType === PriceType.SUBSCRIPTION
                    ? intl.formatMessage({id: 'MODAL.PLAN.monthly'})
                    : ''}
                </span>
              </div>
              <div className='text-muted mb-4'>{plan.desc || plan.shortDesc}</div>
              {/* <div className='text-primary fw-semibold mb-3'>{rate}</div> */}
              {renderFeatures(plan)}
              {renderActionButton(plan)}
            </div>
          </div>
        )
      })}
    </div>
  )

  const renderCustomPlanIntro = () => {
    const customPlan = plans.find((plan) => plan.id === CUSTOM_PLAN)
    if (!customPlan) return null
    return (
      <div className='card shadow-sm border border-dashed border-primary p-6 mt-5'>
        <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3'>
          <div>
            <div className='text-primary text-uppercase fw-bold fs-7 letter-spacing-1 mb-2 text-start'>
              {intl.formatMessage({id: 'MODAL.PLAN.custom_title', defaultMessage: 'Custom plan'})}
            </div>
            <h4 className='fw-bolder mb-2 text-dark text-start'>
              {customPlan.name || intl.formatMessage({id: 'MODAL.PLAN.CURRENCY.' + CUSTOM_PLAN})}
            </h4>
            <div className='text-muted'>
              {intl.formatMessage({
                id: 'MODAL.PLAN.custom_desc',
                defaultMessage:
                  'Have a huge request? Talk to us for a tailored price that beats standard rates.',
              })}
            </div>
          </div>
          <Button
            variant='primary'
            className='fw-bold px-6 py-3'
            disabled={customActionDisabled}
            onClick={() => handlePlanAction(customPlan)}
          >
            {intl.formatMessage({id: 'MODAL.PLAN.call_btn'})}
          </Button>
        </div>
      </div>
    )
  }

  const renderSelectedPlans = () => {
    if (planType === PlanType.ADD_ON) {
      return renderAddonCards()
    }

    const payAsYouGoGroups = isVirtualStaging
      ? payAsYouGoGroupDefs.filter((group) => group.key === 'PAY')
      : payAsYouGoGroupDefs
    const groups = planType === PlanType.SUBSCRIPTION ? subscriptionGroupDefs : payAsYouGoGroups
    const selections = planType === PlanType.SUBSCRIPTION ? groupSelections : payGoSelections
    const setSelections =
      planType === PlanType.SUBSCRIPTION ? setGroupSelections : setPayGoSelections

    return (
      <>
        {renderGroupedCards(groups, selections, setSelections)}
        {planType === PlanType.SUBSCRIPTION && renderCustomPlanIntro()}
      </>
    )
  }

  return (
    <div className='card mb-10'>
      <div className='card-body p-10'>
        <div className='text-center mb-10'>
          <div className='pt-0 pb-10 px-5 px-xl-20'>
            <div className='mb-13'>
              <div className='d-flex flex-column gap-4'>
                <div className='flex-grow-1 text-center'>
                  <h1 className='mb-3 text-center'>
                    {intl.formatMessage({id: 'MODAL.PLAN.title'})}
                  </h1>

                  <div className='text-muted fw-bold fs-5 text-center'>
                    {intl.formatMessage({id: 'MODAL.PLAN.sub_title'})}
                    <a
                      href={process.env.REACT_APP_WEBSITE + '/credit-guidance'}
                      target='_blank'
                      rel='noreferrer'
                      className='link-primary fw-bolder ms-1'
                    >
                      {intl.formatMessage({id: 'MODAL.PLAN.price_guide'})}
                    </a>
                  </div>
                  {currency === CurrencyCode.JPY && (
                    <div className='text-danger fw-bold fs-7 text-center '>
                      {intl.formatMessage({id: 'MODAL.PLAN.tax_guide'})}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='d-flex flex-column'>
              <div className='d-flex justify-content-center mb-8'>
                <div className='bg-light p-1 d-inline-flex gap-2'>
                  {planToggleOptions.map((item, index) => (
                    <button
                      key={index}
                      type='button'
                      className={clsx(
                        'btn btn-sm px-7 py-3 fw-bolder text-uppercase letter-spacing-1',
                        item === planType
                          ? 'btn-primary text-white shadow-lg hover-scale'
                          : 'btn-light-primary text-primary border border-primary border-opacity-25 hover-scale bg-white',
                        item === planType || hoveredPlanToggle === item
                          ? 'text-white'
                          : 'text-primary'
                      )}
                      onClick={() => handlePlanTypeChange(item)}
                      onMouseEnter={() => setHoveredPlanToggle(item)}
                      onMouseLeave={() => setHoveredPlanToggle(undefined)}
                    >
                      {item === 'SUBSCRIPTION'
                        ? intl.formatMessage({id: 'MODAL.PLAN.month'})
                        : intl.formatMessage({id: 'MODAL.PLAN.' + item})}
                    </button>
                  ))}
                </div>
              </div>
              {services.length > 0 && (
                <div className='d-flex flex-column gap-2'>
                  <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
                    <div className='d-flex flex-column gap-2'>
                      <span className='text-muted fw-semibold fs-8 text-uppercase text-start'>
                        {intl.formatMessage({
                          id: 'MODAL.PLAN.service_filter',
                          defaultMessage: 'Service rate',
                        })}
                      </span>
                      <div className='d-flex flex-wrap gap-2'>
                        {services
                          .filter((service) =>
                            ['image_enhance', 'virtual_staging'].includes(service.name)
                          )
                          .map((service, idx) => {
                            const isActive = service.id === selectedServiceId
                            const colorIndex = idx % 5
                            const colorMap: Record<number, string> = {
                              0: 'primary',
                              1: 'success',
                              2: 'info',
                              3: 'danger',
                              4: 'warning',
                            }
                            const color = colorMap[colorIndex] ?? 'primary'
                            return (
                              <button
                                key={service.id}
                                type='button'
                                className={clsx(
                                  'btn btn-xs d-inline-flex align-items-center gap-1 px-2 py-1 fs-7',
                                  `btn-light-${color}`,
                                  isActive
                                    ? 'shadow-sm border border-2 border-' + color
                                    : 'opacity-60'
                                )}
                                onClick={() => setSelectedServiceId(service.id)}
                                style={{
                                  backdropFilter: isActive ? undefined : 'blur(2px)',
                                }}
                              >
                                <span className='fw-bold'>
                                  {intl.formatMessage({id: 'SERVICE.SERVICE.' + service.name})}
                                </span>
                              </button>
                            )
                          })}
                      </div>
                    </div>
                    <div className='d-flex flex-column gap-2 align-items-end'>
                      <span className='text-muted fw-semibold fs-8 text-uppercase text-end'>
                        {intl.formatMessage({
                          id: 'MODAL.PLAN.currency_label',
                          defaultMessage: 'Currency',
                        })}
                      </span>
                      <span className='badge badge-light-primary fs-7'>
                        {intl.formatMessage({id: 'SYSTEM.CURRENCY.' + currency})}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {renderSelectedPlans()}
            </div>

            {/* <div className='d-flex flex-center flex-row-fluid pt-12'>
              <Button variant='secondary' onClick={handleClose} className='btn btn-light me-3'>
                {intl.formatMessage({id: 'MODAL.PLAN.cancel_btn'})}
              </Button>
              <Button
                onClick={handleUpgradePlanClick}
                disabled={
                  loading || selectedPlan?.id === user?.planId || planType === PlanType.ADD_ON
                }
                type='submit'
                variant='primary'
              >
                {loading ? (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {intl.formatMessage({id: 'Login.form.WAITING'})}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : planType === PlanType.PAY_AS_U_GO ? (
                  intl.formatMessage({id: 'MODAL.PLAN.buy_btn'})
                ) : selectedPlan?.id === CUSTOM_PLAN ? (
                  intl.formatMessage({id: 'MODAL.PLAN.call_btn'})
                ) : (
                  intl.formatMessage({id: 'MODAL.PLAN.upgrade_btn'})
                )}
              </Button>
            </div> */}
          </div>
        </div>
      </div>
      <PaymentModal
        isShow={isShowPaymentDetail}
        onClose={() => setIsShowPaymentDetail(false)}
        onOk={(paymentMethod: PAYMENT_METHOD) => {
          setIsShowPaymentDetail(false)
          dispatch(buySubscription({currency, paymentMethod}))
        }}
        paymentDetail={[
          {
            itemName: selectedPlan?.name ?? '',
            unitPrice:
              selectedPlan?.priceData?.find((price) => price.currency === currency)?.price ?? 0,
            quantity: 1,
            currency: currency,
            paymentType: selectedPlan?.priceType,
          },
        ]}
      />
      <Modal
        size='lg'
        aria-labelledby='contained-modal-title-vcenter'
        centered
        show={isShowCancelConfirm}
        onHide={handleCloseCancelConfirm}
        backdrop='static'
      >
        <Modal.Header closeButton>
          <p className='card-label fw-bolder fs-1 mb-1'>
            {intl.formatMessage({id: 'SYSTEM.MODAL.CONFIRM.TITLE'})}
          </p>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-5'>
            <div className='text-muted'>
              {intl.formatMessage({
                id: 'MODAL.PLAN.change_to_paygo_confirm',
                defaultMessage: 'Are you sure you want to switch to Pay as you go?',
              })}
            </div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({
                  id: 'MODAL.PLAN.cancel_reason_label',
                  defaultMessage: 'Reason for cancellation',
                })}
              </label>
              <select
                className={clsx(
                  'form-select form-select-solid',
                  cancelReasonTouched && !cancelReason && 'is-invalid'
                )}
                value={cancelReason}
                onChange={(e) => {
                  setCancelReason(e.target.value)
                  if (e.target.value) {
                    setCancelReasonTouched(false)
                  }
                }}
              >
                <option value='' disabled>
                  {intl.formatMessage({id: 'MODAL.PLAN.select_placeholder'})}
                </option>
                {cancelReasonOptions.map((option) => (
                  <option value={option} key={option}>
                    {intl.formatMessage({id: option})}
                  </option>
                ))}
              </select>
              {cancelReasonTouched && !cancelReason && (
                <div className='invalid-feedback d-block'>
                  {intl.formatMessage({
                    id: 'MODAL.PLAN.cancel_reason_required',
                    defaultMessage: 'Please select a reason.',
                  })}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end flex-row-fluid'>
            <Button
              type='button'
              variant='secondary'
              onClick={handleCloseCancelConfirm}
              className='btn btn-light me-3'
            >
              {intl.formatMessage({id: 'SYSTEM.MODAL.CONFIRM.CANCEL'})}
            </Button>
            <Button type='button' variant='primary' onClick={handleConfirmCancelSubscription}>
              {intl.formatMessage({id: 'SYSTEM.MODAL.CONFIRM.OK'})}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Cancel Add-On Confirmation Modal */}
      <Modal
        aria-labelledby='cancel-addon-modal'
        centered
        show={isShowCancelAddonConfirm}
        onHide={handleCloseCancelAddonConfirm}
        backdrop='static'
      >
        <Modal.Header closeButton>
          <p className='card-label fw-bolder fs-1 mb-1'>
            {intl.formatMessage({id: 'MODAL.PLAN.cancel_addon_title'})}
          </p>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-4'>
            <div className='text-muted'>
              {intl.formatMessage(
                {id: 'MODAL.PLAN.cancel_addon_confirm'},
                {addonName: cancelAddonTarget?.plan?.name || ''}
              )}
            </div>
            <div className='alert alert-warning d-flex align-items-center p-4 '>
              <i className='bi bi-exclamation-triangle-fill fs-3  me-3'></i>
              {intl.formatMessage({id: 'MODAL.PLAN.cancel_addon_warning'})}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end flex-row-fluid'>
            <Button
              type='button'
              variant='secondary'
              onClick={handleCloseCancelAddonConfirm}
              className='btn btn-light me-3'
            >
              {intl.formatMessage({id: 'SYSTEM.MODAL.CONFIRM.CANCEL'})}
            </Button>
            <Button
              type='button'
              variant='danger'
              onClick={handleConfirmCancelAddon}
              disabled={loading}
            >
              {loading ? (
                <span className='spinner-border spinner-border-sm me-2' role='status' />
              ) : null}
              {intl.formatMessage({id: 'MODAL.PLAN.cancel_addon_btn'})}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        size='lg'
        aria-labelledby='contained-modal-title-vcenter'
        centered
        show={isShowContactModal}
        onHide={() => setIsShowContactModal(false)}
        backdrop='static'
      >
        <Modal.Header closeButton>
          <p className='card-label fw-bolder fs-2 mb-1'>
            {intl.formatMessage({id: 'MODAL.PLAN.contact_title'})}
          </p>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-5'>
            <div className='text-muted'>{intl.formatMessage({id: 'MODAL.PLAN.contact_desc'})}</div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({id: 'MODAL.PLAN.company_name'})}
              </label>
              <input
                type='text'
                className={clsx(
                  'form-control form-control-solid',
                  isContactFieldInvalid(contactForm.companyName) && 'is-invalid'
                )}
                value={contactForm.companyName}
                onChange={(e) => handleContactFieldChange('companyName', e.target.value)}
              />
            </div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({id: 'MODAL.PLAN.company_address'})}
              </label>
              <input
                type='text'
                className={clsx(
                  'form-control form-control-solid',
                  isContactFieldInvalid(contactForm.companyAddress) && 'is-invalid'
                )}
                value={contactForm.companyAddress}
                onChange={(e) => handleContactFieldChange('companyAddress', e.target.value)}
              />
            </div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({id: 'MODAL.PLAN.business_field'})}
              </label>
              <select
                className={clsx(
                  'form-select form-select-solid',
                  isContactFieldInvalid(contactForm.businessField) && 'is-invalid'
                )}
                value={contactForm.businessField}
                onChange={(e) => handleContactFieldChange('businessField', e.target.value)}
              >
                <option value='' disabled>
                  {intl.formatMessage({id: 'MODAL.PLAN.select_placeholder'})}
                </option>
                {businessFieldOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({id: 'MODAL.PLAN.company_size'})}
              </label>
              <select
                className={clsx(
                  'form-select form-select-solid',
                  isContactFieldInvalid(contactForm.companySize) && 'is-invalid'
                )}
                value={contactForm.companySize}
                onChange={(e) => handleContactFieldChange('companySize', e.target.value)}
              >
                <option value='' disabled>
                  {intl.formatMessage({id: 'MODAL.PLAN.select_placeholder'})}
                </option>
                {companySizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({id: 'MODAL.PLAN.processing_volume'})}
              </label>
              <select
                className={clsx(
                  'form-select form-select-solid',
                  isContactFieldInvalid(contactForm.processingVolume) && 'is-invalid'
                )}
                value={contactForm.processingVolume}
                onChange={(e) => handleContactFieldChange('processingVolume', e.target.value)}
              >
                <option value='' disabled>
                  {intl.formatMessage({id: 'MODAL.PLAN.select_placeholder'})}
                </option>
                {processingVolumeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({id: 'MODAL.PLAN.message'})}
              </label>
              <textarea
                className='form-control form-control-solid'
                rows={4}
                value={contactForm.message}
                onChange={(e) => handleContactFieldChange('message', e.target.value)}
              />
            </div>
            {contactSubmitAttempted && !isContactFormValid && (
              <div className='text-danger fs-7'>
                {intl.formatMessage({id: 'MODAL.PLAN.required_notice'})}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end flex-row-fluid'>
            <Button
              type='button'
              variant='secondary'
              onClick={() => setIsShowContactModal(false)}
              className='btn btn-light me-3'
            >
              {intl.formatMessage({id: 'MODAL.PLAN.cancel_btn'})}
            </Button>
            <Button
              type='button'
              variant='primary'
              disabled={customActionDisabled}
              onClick={handleSubmitContactRequest}
            >
              {intl.formatMessage({id: 'MODAL.PLAN.call_btn'})}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PricingPage
